import { useEffect, useState } from "react";
import { Flame, Loader2, Twitter, Link as LinkIcon, Video, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import DeathCard from "@/components/game/DeathCard";
import RewardedAdContinue from "@/components/game/RewardedAdContinue";
import ReviveGrantedBanner from "@/components/game/ReviveGrantedBanner";
import AdPlayingOverlay from "@/components/game/AdPlayingOverlay";
import EssenceBurst from "@/components/game/EssenceBurst";
import { handleReviveAd, handleAdFreeRevive, hasUsedMonetagRevive, MONETAG_REVIVE_URL, showRestartAd, resetMonetagReviveGuard } from "@/lib/adManager";
import { isRewardedReady } from "@/lib/h5ads";

export default function GameOverScreen({ run, onNewRun, onOpenStore, livesRemaining, onAdRevived, reviveStreak = 0, adFreeRevives = 0, onEssenceEarned }) {
  const [reviving, setReviving] = useState(false);
  const [monetagBusy, setMonetagBusy] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [monetagUsed, setMonetagUsed] = useState(() => hasUsedMonetagRevive());
  const [reviveGranted, setReviveGranted] = useState(null); // { floor, health, max_health } | null
  const [card, setCard] = useState(null);
  const [goldDoubled, setGoldDoubled] = useState(false);
  const [doublerBusy, setDoublerBusy] = useState(false);

  useEffect(() => {
    if (!run?.share_id) return;
    let cancelled = false;
    api.get(`/cards/${run.share_id}`)
      .then((c) => { if (!cancelled) setCard(c); })
      .catch(() => { /* card fetch failed — not fatal */ });
    return () => { cancelled = true; };
  }, [run?.share_id]);

  // Pre-flight: if the rewarded SDK isn't ready (Monetag auto-serve zone with
  // no show_<zone>() fn), surface the one-click FREE REVIVE directly instead
  // of forcing players through a "watch ad" button that always fails.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!isRewardedReady()) setPopupBlocked(true);
    }, 2000);
    return () => clearTimeout(t);
  }, []);

  const handleRevive = async () => {
    setReviving(true);
    try {
      const res = await api.post("/store/checkout", {
        product_key: "second_wind",
        origin_url: window.location.origin,
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Second Wind unavailable");
      setReviving(false);
    }
  };

  const handleGoldDoubler = async () => {
    if (doublerBusy || goldDoubled) return;
    const runId = run?.run_id || run?.share_run_id;
    if (!runId) {
      toast.error("Run reference missing.");
      return;
    }
    setDoublerBusy(true);
    // Pre-warm the ad (sync with user click so browsers don't flag)
    const { handleReviveAd: _r } = await import("@/lib/adManager");  // reuse the H5 ad flow
    const { showRewarded } = await import("@/lib/h5ads");
    const ad = await showRewarded({ placement: "gold_doubler" });
    if (!ad.rewarded) {
      toast.error(ad.reason === "dismissed" ? "Ad closed early — no bonus." : "Ad unavailable.");
      setDoublerBusy(false);
      return;
    }
    try {
      const r = await api.post("/ads/reward/gold-doubler", { run_id: runId, ad_token: ad.token });
      if (r?.ok) {
        toast.success(`+${r.bonus_essence} essence banked for the Forge.`);
        setGoldDoubled(true);
        onEssenceEarned?.(r);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Doubler failed.");
    }
    setDoublerBusy(false);
  };

  const handleMonetagRevive = async () => {
    if (monetagBusy || monetagUsed) return;
    setPopupBlocked(false);
    setMonetagBusy(true);
    // If player has an Ad-Free Pass with credits, skip the ad flow entirely.
    const result = adFreeRevives > 0
      ? await handleAdFreeRevive()
      : await handleReviveAd();
    if (result.ok) {
      const msgPrefix = result.data.used_ad_free ? "Ad-Free Revive · " : "";
      toast.success(`${msgPrefix}Revived on floor ${result.data.floor} with ${result.data.health} HP.`);
      setMonetagUsed(true);
      setReviveGranted({
        floor:      result.data.floor,
        health:     result.data.health,
        max_health: result.data.max_health,
        payload:    result.data,
      });
    } else if (result.reason === "popup_blocked" || result.reason === "ad_unavailable") {
      setPopupBlocked(true);
      toast.error("Ad unavailable — try the free fallback revive below.");
    } else if (result.reason === "dismissed") {
      // User closed the ad early — no reward, but no error either
      toast.info("Ad closed early — reward not granted.");
    } else if (result.reason === "already_used") {
      setMonetagUsed(true);
      toast.info("You've already used your revive this run.");
    } else {
      toast.error(result.detail || "Revive unavailable.");
    }
    setMonetagBusy(false);
  };

  return (
    <div className="panel p-10 text-center fade-up relative overflow-hidden" data-testid="game-over-screen">
      <EssenceBurst amount={run?.essence_earned || 0} />
      <p className="text-xs tracking-[0.5em] blood-text">PERMADEATH</p>
      <h2 className="font-heading text-5xl md:text-6xl mt-3 text-[#f0e6d2] italic">
        You fell on floor <span className="blood-text">{run.floor}</span>
      </h2>
      <p className="font-narrative text-lg mt-4 ash-text max-w-xl mx-auto">
        {run.current_narrative?.replace(/\*\*|__|\*/g, "")}
      </p>

      <div className="mt-8 grid grid-cols-3 max-w-md mx-auto gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] ash-text">FLOORS</div>
          <div className="font-heading text-3xl arcane-text">{run.floor}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] ash-text">GOLD</div>
          <div className="font-heading text-3xl gold-text">{run.gold}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] ash-text">TURNS</div>
          <div className="font-heading text-3xl text-[#f0e6d2]">{run.turn}</div>
        </div>
      </div>

      {/* Shareable Death Card */}
      {card && (
        <div className="mt-8 text-left">
          <DeathCard card={card} embedded livesRemaining={livesRemaining} />
        </div>
      )}

      {/* MONETAG REVIVE — user-requested opt-in ad revive (50% max HP, 1 per run) */}
      {MONETAG_REVIVE_URL && !monetagUsed && (
        <div
          className="mt-8 max-w-md mx-auto panel p-6 relative overflow-hidden"
          data-testid="monetag-revive-panel"
          style={{
            borderColor: "#d4af37",
            boxShadow: "0 0 40px rgba(212,175,55,0.30)",
            background: "linear-gradient(180deg, rgba(30,20,5,0.85), rgba(10,7,2,0.95))",
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none opacity-40"
            style={{
              background: "radial-gradient(200px 60px at 50% 50%, rgba(212,175,55,0.35), transparent 70%)",
              animation: "monetag-glow 2.6s ease-in-out infinite",
            }}
          />
          <div className="relative z-10">
            <div className="flex items-center justify-center gap-2 gold-text">
              <Video size={14} />
              <span className="text-[10px] tracking-[0.4em]">
                {popupBlocked ? "THE EMBER'S GIFT" : "A SPONSOR\u2019S BARGAIN"}
              </span>
              <Video size={14} />
            </div>
            <div className="font-heading text-3xl mt-2 text-[#f0e6d2] text-center italic">
              {popupBlocked
                ? <>Second <span className="ember">Chance</span></>
                : adFreeRevives > 0
                  ? <>Revive <span className="gold-text">(Ad-Free · {adFreeRevives} left)</span></>
                  : <>Revive <span className="gold-text">(Watch Ad)</span></>}
            </div>
            {reviveStreak > 0 && (
              <div
                className="mx-auto mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] tracking-[0.3em]"
                data-testid="monetag-streak-badge"
                style={{
                  background: "linear-gradient(90deg, rgba(226,88,34,0.25), rgba(212,175,55,0.25))",
                  border: "1px solid rgba(212,175,55,0.6)",
                  boxShadow: "0 0 20px rgba(226,88,34,0.35)",
                  color: "#ffd8b8",
                }}
              >
                🔥 STREAK x{reviveStreak} · NEXT REVIVE = {Math.min(85, 50 + reviveStreak * 7)}% HP
              </div>
            )}
            <p className="font-narrative text-sm ash-text mt-2 italic text-center">
              Return to floor {run.floor} at half strength ({Math.round((run.max_health || 100) * (Math.min(0.85, 0.50 + reviveStreak * 0.07)))}/{run.max_health || 100} HP)
              {reviveStreak > 0 ? " + streak bonus" : ""}.
              <br />
              <span className="text-[10px] tracking-widest">
                {popupBlocked
                  ? "KEEPS GOLD, INVENTORY & BOSS FIGHT"
                  : adFreeRevives > 0
                    ? "AD-FREE PASS ACTIVE · KEEPS GOLD, INVENTORY & BOSS FIGHT"
                    : "ONE REVIVE PER RUN · KEEPS GOLD, INVENTORY & BOSS FIGHT"}
              </span>
            </p>
            {popupBlocked ? (
              // Ad SDK can't be manually triggered — surface FREE REVIVE as the one-click
              // primary action. No alarming red boxes; players feel taken care of.
              <>
                <button
                  onClick={async () => {
                    if (monetagBusy) return;
                    setMonetagBusy(true);
                    try {
                      const res = await api.post("/ads/reward/continue-fallback", {});
                      setMonetagUsed(true);
                      setReviveGranted({
                        floor: res.floor, health: res.health, max_health: res.max_health, payload: res,
                      });
                      toast.success(
                        `The Ember lights your torch anyway. ${res.used_today}/${res.daily_limit} free revives today.`,
                        { duration: 5000 }
                      );
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Revive failed.");
                    }
                    setMonetagBusy(false);
                  }}
                  disabled={monetagBusy}
                  data-testid="go-free-revive-btn"
                  className="mt-5 w-full py-3 flex items-center justify-center gap-3 rounded-sm font-heading tracking-widest text-base transition-all"
                  style={{
                    background: "linear-gradient(135deg, #e25822 0%, #ffb458 50%, #e25822 100%)",
                    border: "2px solid #ffd8b8",
                    color: "#1a0805",
                    boxShadow: "0 0 32px rgba(226,88,34,0.75), inset 0 0 20px rgba(255,183,88,0.4)",
                    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    fontWeight: 700,
                  }}
                >
                  {monetagBusy ? (
                    <><Loader2 size={16} className="animate-spin" /> LIGHTING THE EMBER...</>
                  ) : (
                    <>🔥 FREE REVIVE · ON US</>
                  )}
                </button>
                <div className="mt-3 text-center text-[10px] tracking-[0.3em] ash-text opacity-70">
                  2 FREE REVIVES PER DAY · KEEPS GOLD, INVENTORY & FLOOR
                </div>
                {onOpenStore && (
                  <div className="mt-2 text-center">
                    <button
                      onClick={() => onOpenStore?.("ad_free_pass")}
                      className="text-[10px] tracking-[0.3em] gold-text underline hover:text-[#f0e6d2]"
                      data-testid="offer-ad-free-pass-btn"
                    >
                      SKIP ADS FOREVER
                    </button>
                  </div>
                )}
              </>
            ) : (
              <button
                onClick={handleMonetagRevive}
                disabled={monetagBusy}
                data-testid="monetag-revive-btn"
                className="btn-primary mt-5 w-full text-base flex items-center justify-center gap-2"
                style={{ borderColor: "#d4af37" }}
              >
                {monetagBusy ? (
                  <><Loader2 size={14} className="animate-spin" /> STRIKING THE PACT...</>
                ) : adFreeRevives > 0 ? (
                  <>✨ CLAIM FREE REVIVE ({adFreeRevives} LEFT)</>
                ) : (
                  <><Video size={16} /> REVIVE (WATCH AD)</>
                )}
              </button>
            )}
          </div>
          <style>{`
            @keyframes monetag-glow {
              0%,100% { opacity: 0.28; }
              50%     { opacity: 0.55; }
            }
          `}</style>
        </div>
      )}

      {/* 2x RESOURCE DOUBLER — rewarded ad to double this run's gold into essence */}
      {run?.gold > 0 && (
        <div
          className="mt-4 max-w-md mx-auto panel p-4"
          data-testid="gold-doubler-panel"
          style={{ borderColor: "rgba(100,160,220,0.5)" }}
        >
          <div className="text-center">
            <div className="text-[10px] tracking-[0.4em] mb-1" style={{ color: "#64a0dc" }}>
              — THE FORGE WANTS MORE —
            </div>
            <div className="font-heading text-lg text-[#f0e6d2]">
              2x <span style={{ color: "#64a0dc" }}>Essence</span> Doubler
            </div>
            <p className="text-[11px] ash-text italic mt-1">
              Watch a short ad to bank <span className="gold-text">+{run.gold || 0} essence</span> for permanent HP/Armor upgrades.
            </p>
            <button
              onClick={handleGoldDoubler}
              disabled={goldDoubled || doublerBusy || !(run.gold > 0)}
              data-testid="gold-doubler-btn"
              className="btn-primary mt-3 w-full disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ borderColor: "#64a0dc" }}
            >
              {doublerBusy ? (
                <><Loader2 size={14} className="animate-spin inline mr-2" /> AD LOADING...</>
              ) : goldDoubled ? (
                "✓ ESSENCE BANKED"
              ) : (
                <><Video size={14} className="inline mr-2" /> DOUBLE THE SPOILS (WATCH AD)</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* SECOND WIND — the impulse purchase */}
      <div className="mt-10 max-w-md mx-auto panel p-6 border-[#e25822] shadow-[0_0_40px_rgba(226,88,34,0.35)]" data-testid="second-wind-panel">
        <div className="flex items-center justify-center gap-2 ember">
          <Flame size={16} />
          <span className="text-[10px] tracking-[0.4em]">ONE OFFER · ONE PACT</span>
          <Flame size={16} />
        </div>
        <div className="font-heading text-4xl mt-2 text-[#f0e6d2] italic">Second <span className="gold-text">Wind</span></div>
        <p className="font-narrative text-base ash-text mt-2 italic">
          Return to floor {run.floor} — same hero, same gold, half-mended wounds.
        </p>
        <button
          onClick={handleRevive}
          disabled={reviving}
          data-testid="second-wind-btn"
          className="btn-primary mt-5 w-full text-base flex items-center justify-center gap-2"
          style={{ borderColor: "#e25822", color: "#fff", background: "linear-gradient(180deg, #3a1408, #1a0805)" }}
        >
          {reviving ? <><Loader2 size={14} className="animate-spin" /> REDIRECTING TO STRIPE...</> : "▶  REVIVE FOR $0.99"}
        </button>
        <p className="text-[10px] ash-text tracking-widest mt-2">SECURE CHECKOUT · POWERED BY STRIPE</p>
      </div>

      {/* Free rewarded revive is handled by the Monetag panel above.
          We removed the Google H5 AdSense fallback (RewardedAdContinue)
          because it commonly renders "AD SERVICE UNAVAILABLE" when
          AdSense hasn't approved the domain, which drives players away. */}

      {/* Social share — pre-filled boast so friends can beat the run */}
      {run.share_id && (
        <div className="mt-8 max-w-md mx-auto" data-testid="share-panel">
          <div className="text-[10px] tracking-[0.4em] ash-text mb-3">— TELL THE WORLD —</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <ShareToXButton run={run} />
            <CopyLinkButton run={run} />
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
        <button
          onClick={async () => {
            // Google H5 policy: `type:'start'` ad break at restart. Non-blocking —
            // resolves even if no ad fills, so start-of-run is never gated.
            resetMonetagReviveGuard();
            if (window.__adTagDirty) { window.location.reload(); return; }
            await showRestartAd();
            onNewRun?.();
          }}
          disabled={livesRemaining <= 0}
          className="btn-glow"
          data-testid="new-run-btn"
        >
          {livesRemaining > 0 ? "BEGIN A NEW SOUL" : "NO LIVES LEFT"}
        </button>
        <button onClick={onOpenStore} className="btn-glow" data-testid="open-store-btn">
          BUY EXTRA LIVES
        </button>
      </div>

      <ReviveGrantedBanner
        open={!!reviveGranted}
        floor={reviveGranted?.floor}
        health={reviveGranted?.health}
        maxHealth={reviveGranted?.max_health}
        onContinue={() => {
          const payload = reviveGranted?.payload;
          setReviveGranted(null);
          onAdRevived?.(payload);
        }}
      />
      <AdPlayingOverlay
        open={(monetagBusy && adFreeRevives === 0) || doublerBusy}
        label={doublerBusy ? "SPONSORED BREAK · DOUBLING YOUR SPOILS" : "REWARDED AD · WATCH TO REVIVE"}
      />
    </div>
  );
}

/**
 * Twitter/X intent link — pre-fills a boast tuned to convert curious friends into players.
 * No SDK, no auth. Just an intent URL. Uses window.open target=_blank so the game state
 * on the tab stays intact (they'll come back to try Second Wind).
 */
function ShareToXButton({ run }) {
  const url = `${window.location.origin}/card/${run.share_id}`;
  const cls = (run.class_key || "warrior").replace(/^./, c => c.toUpperCase());
  const text = `I fell to the Ember King on Floor ${run.floor} as a ${cls}. Can you go deeper? #DawnVanguard`;
  const intent = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
  return (
    <a
      href={intent}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="share-x-btn"
      className="btn-glow flex-1 flex items-center justify-center gap-2"
      style={{ background: "linear-gradient(180deg, #0f1419, #050a0f)", borderColor: "#1d9bf0" }}
    >
      <Twitter size={14} style={{ color: "#1d9bf0" }} />
      <span>SHARE ON X</span>
    </a>
  );
}

function CopyLinkButton({ run }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/card/${run.share_id}`;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Death card link copied — spread the fear.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Older browsers / iframes without clipboard permission — fall back to selection.
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Death card link copied."); setCopied(true); }
      catch { toast.error("Could not copy link."); }
      document.body.removeChild(ta);
    }
  };
  return (
    <button
      onClick={onCopy}
      data-testid="share-copy-link-btn"
      className="btn-glow flex-1 flex items-center justify-center gap-2"
    >
      <LinkIcon size={14} className="gold-text" />
      <span>{copied ? "COPIED" : "COPY LINK"}</span>
    </button>
  );
}
